import { useAccount } from "@qw/auth";
import { isInternal, KnownInboxLabel } from "@qw/qw-common";
import {
  AppearancePreferences,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  PreferenceGroup,
  type PreferenceOption,
  PreferenceSelectRow,
  PreferenceToggleRow,
} from "@qw/ui";
import {
  capture,
  PostHogEvent,
  SearchFilterAction,
  SearchFilterSurface,
} from "@third-plane/posthog-integration-interface";
import { useLocation } from "react-router";
import { THREAD_READING_ORDER, type ThreadReadingOrder } from "@/conversation/thread-reading-order";
import { useThreadReadingOrder } from "@/conversation/use-thread-reading-order";
import { INBOX_LIST_THREAD_LAYOUT, type InboxListThreadLayout } from "@/inbox/inbox-list-thread-layout";
import { INBOX_RAIL_LAYOUT } from "@/inbox/inbox-rail-layout";
import { useInboxListThreadLayout } from "@/inbox/use-inbox-list-thread-layout";
import { useInboxRailLayout } from "@/inbox/use-inbox-rail";
import { useInboxFilter } from "@/providers/inbox-filter-provider";

const READING_ORDER_OPTIONS: readonly PreferenceOption<ThreadReadingOrder>[] = [
  { value: THREAD_READING_ORDER.oldestFirst, label: "Oldest first" },
  { value: THREAD_READING_ORDER.newestFirst, label: "Newest first" },
];

const INBOX_LAYOUT_OPTIONS: readonly PreferenceOption<InboxListThreadLayout>[] = [
  { value: INBOX_LIST_THREAD_LAYOUT.sideBySide, label: "Side-by-side" },
  { value: INBOX_LIST_THREAD_LAYOUT.stacked, label: "Stacked" },
];

/**
 * The labels the server can boost to the top of the conversation list. `priorityLabels` reorders
 * rather than excludes — it leaves `totalCount` alone — so these rows are worded as sorting.
 */
const PRIORITY_SORT_ROWS: readonly { id: string; label: string; value: string }[] = [
  { id: "sort-starred-first", label: "Sort starred to top", value: KnownInboxLabel.STARRED },
  { id: "sort-unread-first", label: "Sort unread to top", value: KnownInboxLabel.UNREAD },
];

const PRIORITY_SORT_RANK = PRIORITY_SORT_ROWS.map(({ value }) => value);

/**
 * Keeps the stored selection in row order, so reaching the same pair of choices in either order
 * yields one array rather than two.
 */
const togglePriorityLabel = (labels: string[], label: string, selected: boolean) =>
  selected
    ? [...labels, label].sort((a, b) => PRIORITY_SORT_RANK.indexOf(a) - PRIORITY_SORT_RANK.indexOf(b))
    : labels.filter((existing) => existing !== label);

interface InboxSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The one home for the inbox's sticky, device-scoped preferences.
 *
 * `UserMenu` renders this itself rather than opening it through `useModal()`: `ModalProvider` sits
 * above `RouterProvider` in `app.tsx`, so a modal opened that way mounts outside every route-level
 * provider a preference row might need to read.
 *
 * Every row here reads its own stored preference and nothing else, so a row can name a setting that
 * only one surface acts on — `Message order` does nothing outside a thread, and the rail layout does
 * nothing outside `/inbox` — without having to know where the broker currently is.
 *
 * Rows are grouped by what the preference changes, and a new one belongs in the group that names its
 * effect: `Appearance` how the app looks, `Reading` how a thread reads, `Layout` where panels sit,
 * `Sorting` what order the conversation list arrives in. B-1861 shipped three rows flat and pinned
 * that with a test; B-1897 reversed it at seven.
 */
export const InboxSettingsDialog = ({ open, onOpenChange }: InboxSettingsDialogProps) => {
  const { user } = useAccount();
  const { readingOrder, setReadingOrder } = useThreadReadingOrder();
  const { isMergedRail, setRailLayout } = useInboxRailLayout();
  const { listThreadLayout, setListThreadLayout } = useInboxListThreadLayout();
  const { priorityLabels, setPriorityLabels } = useInboxFilter();
  const location = useLocation();

  const handlePriorityLabel = (label: string, selected: boolean) => {
    // Non-PII: label is a known enum (STARRED/UNREAD), never a custom label. The event keeps its
    // `SearchFilter*` name on purpose — renaming it would break the only continuous series on how
    // often brokers touch this control.
    capture(PostHogEvent.SearchFilterUsed, {
      routeName: location.pathname,
      surface: SearchFilterSurface.Unknown,
      action: selected ? SearchFilterAction.FilterAdded : SearchFilterAction.FilterRemoved,
      filterKeys: [label],
    });
    setPriorityLabels((previous) => togglePriorityLabel(previous, label, selected));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
          <DialogDescription>These preferences are saved on this device.</DialogDescription>
        </DialogHeader>
        <div className="space-y-6 divide-y">
          <AppearancePreferences internal={isInternal(user)} />
          <PreferenceGroup heading="Reading">
            <PreferenceSelectRow
              id="reading-order"
              label="Message order"
              options={READING_ORDER_OPTIONS}
              value={readingOrder}
              onChange={setReadingOrder}
            />
          </PreferenceGroup>
          <PreferenceGroup heading="Layout">
            <PreferenceSelectRow
              id="inbox-layout"
              label="Conversation list"
              options={INBOX_LAYOUT_OPTIONS}
              value={listThreadLayout}
              onChange={setListThreadLayout}
            />
            <PreferenceToggleRow
              id="rail-layout"
              label="Merge insured into left rail"
              checked={isMergedRail}
              onCheckedChange={(checked) => setRailLayout(checked ? INBOX_RAIL_LAYOUT.merged : INBOX_RAIL_LAYOUT.split)}
            />
          </PreferenceGroup>
          <PreferenceGroup heading="Sorting">
            {PRIORITY_SORT_ROWS.map(({ id, label, value }) => (
              <PreferenceToggleRow
                key={value}
                id={id}
                label={label}
                checked={priorityLabels.includes(value)}
                onCheckedChange={(checked) => handlePriorityLabel(value, checked)}
              />
            ))}
          </PreferenceGroup>
        </div>
      </DialogContent>
    </Dialog>
  );
};
